/*
 * Sticky buy panel — pinned from the top of the viewport until the sibling column has
 * finished scrolling past, i.e. its bottom reaches the bottom of the viewport.
 *
 * Not `position: sticky`: available travel is `max(0, other column - panel)`, which is
 * zero when the panel is the taller column. `.pdp-column` holds the width and height;
 * `.pdp-column__inner` moves, through static -> pinned -> parked.
 *
 * Under the transparent bar it also carries the panel's first row out from under it, on a
 * `translate` held for as long as the bar is on screen.
 *
 * See docs/theme-architecture/sections/product.yaml.
 */
(function () {
  if (window.__pdpPanelSticky) return;
  window.__pdpPanelSticky = true;

  var DESKTOP = '(min-width: 990px)';

  /* The transparent-bar templates pull the shell up under the header; only a column with
     `clear_overlay_header` on pads itself clear of it. */
  function underOverlay(panel) {
    if (panel.classList.contains('pdp-column--clear-header')) return false;
    return !!document.querySelector('.header-section .header[data-overlay="true"]');
  }

  /* Measured, not read from --header-h, which is composed from several tokens. */
  function headerHeight() {
    var header = document.querySelector('.header-section');
    return header ? header.offsetHeight : 0;
  }

  /* The full height stays reserved while the bar is auto-hidden, or the panel jumps. A
     column running under the bar rests at its own top instead and takes the clearance as a
     separate offset, so its first row reads at the top of the page without the panel
     keeping the bar's height for the whole scroll. */
  function restOffset(panel) {
    return underOverlay(panel) ? 0 : headerHeight();
  }

  /* The bar hides itself on the way down the page and comes back on the way up, so this is
     read per frame rather than assumed from the scroll position. Its own handler runs
     before ours, which is rAF-throttled, so the attribute is always current by then. */
  var barEl = null;

  function barHidden() {
    if (!barEl || !barEl.isConnected) barEl = document.querySelector('.header-section .header');
    return !!barEl && barEl.getAttribute('data-hidden') === 'true';
  }

  /* Where the panel's top edge sits in the viewport, whichever way it is being held. */
  function topOf(m, state, y) {
    if (state === 'pinned') return m.rest;
    if (state === 'parked') return m.release + m.rest - y;
    return m.top - y;
  }

  /* The offset follows the bar and not the scroll position: there on load, gone as the bar
     hides itself on the way down, back with it on the way up — which is the whole point,
     since a bar that returns over the panel's first row covers it again.

     Dropped once that first row has left the top of the screen. A panel taller than the
     viewport pins by its bottom edge, so its first row is somewhere above the fold with
     nothing left to cover, and holding the panel down there would only push its last rows
     out of sight. */
  function shiftFor(m, state, y) {
    if (m.reserve === 0 || barHidden()) return 0;
    return topOf(m, state, y) + m.pad >= 0 ? m.reserve : 0;
  }

  /* Translate and not padding, because the inner's height is measured here and watched by a
     ResizeObserver: a top padding that moved with the bar would have the panel re-measuring
     and re-pinning for the length of every transition. Cleared rather than set to zero, or
     the inner stays a containing block for the positioned boxes inside it. */
  function shiftBy(inner, px) {
    var next = Math.max(0, Math.round(px));
    if (inner.__pdpShift === next) return;
    inner.__pdpShift = next;
    inner.style.translate = next > 0 ? '0 ' + next + 'px' : '';
  }

  /* Every column in the panel's row except the panel itself. Nesting is guarded both
     ways so a column inside the panel is never mistaken for a sibling. */
  function siblingsOf(panel) {
    var root = panel.closest('.pdp');
    if (!root) return [];
    var all = root.querySelectorAll('.pdp-column');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i] === panel || all[i].contains(panel) || panel.contains(all[i])) continue;
      out.push(all[i]);
    }
    return out;
  }

  /* The region the panel travels against: every other column, taken to the lowest bottom
     edge among them. On the PDP that is the details stack, which sits below the gallery
     rather than beside it, so the panel runs the length of the section and not just the
     gallery — the accordions are as much a column to travel past as the media is. Alone in
     the row, it falls back to `.pdp`. */
  function endOf(panel) {
    var siblings = siblingsOf(panel);
    var bottom = null;

    for (var i = 0; i < siblings.length; i++) {
      var b = siblings[i].getBoundingClientRect().bottom;
      if (bottom === null || b > bottom) bottom = b;
    }

    if (bottom === null) {
      var root = panel.closest('.pdp');
      if (!root) return null;
      bottom = root.getBoundingClientRect().bottom;
    }

    return bottom + window.pageYOffset;
  }

  function clear(panel, inner) {
    inner.style.position = '';
    inner.style.top = '';
    inner.style.left = '';
    inner.style.width = '';
    panel.style.minHeight = '';
    inner.classList.remove('is-pinned', 'is-parked');
    panel.__pdpState = null;
  }

  function measure(panel, inner) {
    clear(panel, inner);

    if (!window.matchMedia(DESKTOP).matches) {
      panel.__pdpMetrics = null;
      /* Not folded into `clear`, which runs ahead of every re-measure: dropping the offset
         there would have it read back as `none` by the measurements below and animate in
         again on the far side. */
      shiftBy(inner, 0);
      return;
    }

    var rect = panel.getBoundingClientRect();
    var height = inner.offsetHeight;
    var viewport = window.innerHeight;
    var end = endOf(panel);

    var overlay = underOverlay(panel);
    var pad = parseFloat(window.getComputedStyle(inner).paddingTop) || 0;
    var barH = overlay ? headerHeight() : 0;

    /* Subtracted below: the padding is inside the box being positioned, so resting the
       box against the bar would drop the content a further inset under it. A column under
       the bar rests against nothing and keeps its padding, or its top row lands flush
       with the viewport edge instead of where it sat before it pinned. */
    var padTop = overlay ? 0 : pad;

    /* Fits: rest at the offset above. Taller than the screen: rest on the bottom edge,
       so the last rows cannot be stranded below the fold. */
    var rest = Math.min(restOffset(panel) - padTop, viewport - height);

    panel.__pdpMetrics = {
      top: rect.top + window.pageYOffset,
      left: rect.left,
      width: rect.width,
      height: height,
      rest: rest,
      /* Where the panel's own bottom edge meets the region's. Pinned, that edge sits
         `rest + height` below the top of the viewport, so this is the scroll position at
         which travelling any further would overhang the region. Worked from the panel and
         not the viewport, or a panel resting against the top releases a whole viewport
         early and leaves the space beneath it empty for the rest of the section. */
      release: end === null ? null : end - height - rest,
      /* How far the panel has to sit below the bar, and where its first row starts inside
         it — only what the column's own top padding does not already cover. */
      reserve: Math.max(0, barH - pad),
      pad: pad,
    };

    panel.style.minHeight = height + 'px';
    apply(panel, inner);
  }

  function apply(panel, inner) {
    var m = panel.__pdpMetrics;
    if (!m) return;

    var start = m.top - m.rest;
    var y = window.pageYOffset;
    var state = 'static';

    /* Window closes before it opens = the region beside the panel is shorter than the
       panel, so there is nothing to pin through. Both ends carry the same rest offset, so
       what this compares is those two heights and nothing else. */
    if (m.release !== null && m.release > start) {
      if (y >= m.release) state = 'parked';
      else if (y > start) state = 'pinned';
    }

    /* Ahead of the early return: the bar can hide and return without the panel changing
       state, and that is exactly when the offset has to move. */
    shiftBy(inner, shiftFor(m, state, y));

    if (panel.__pdpState === state) return;
    panel.__pdpState = state;

    inner.classList.toggle('is-pinned', state === 'pinned');
    inner.classList.toggle('is-parked', state === 'parked');

    if (state === 'pinned') {
      inner.style.position = 'fixed';
      inner.style.top = Math.round(m.rest) + 'px';
      inner.style.left = Math.round(m.left) + 'px';
      inner.style.width = Math.round(m.width) + 'px';
    } else if (state === 'parked') {
      inner.style.position = 'absolute';
      inner.style.top = Math.round(m.release + m.rest - m.top) + 'px';
      inner.style.left = '0';
      inner.style.width = '100%';
    } else {
      inner.style.position = '';
      inner.style.top = '';
      inner.style.left = '';
      inner.style.width = '';
    }
  }

  var panels = [];
  var queued = false;

  function onScroll() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      for (var i = 0; i < panels.length; i++) apply(panels[i][0], panels[i][1]);
    });
  }

  function remeasure() {
    for (var i = 0; i < panels.length; i++) measure(panels[i][0], panels[i][1]);
  }

  function watch(scope) {
    var found = scope.querySelectorAll('[data-pdp-panel]');

    for (var i = 0; i < found.length; i++) {
      var panel = found[i];
      var inner = panel.querySelector('[data-pdp-panel-inner]');
      if (!inner || panel.__pdpWatched) continue;
      panel.__pdpWatched = true;
      panels.push([panel, inner]);

      /* Both heights move at runtime — status line, upsell images, a variant change. */
      if (typeof ResizeObserver === 'undefined') continue;
      var observer = new ResizeObserver(remeasure);
      observer.observe(inner);
      var siblings = siblingsOf(panel);
      for (var s = 0; s < siblings.length; s++) observer.observe(siblings[s]);
    }

    remeasure();

    /* A frame late: the offset a panel opens with has to be in place before the transition
       exists, or it animates in from nothing on load. */
    window.requestAnimationFrame(function () {
      for (var p = 0; p < panels.length; p++) panels[p][1].classList.add('is-shift-live');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watch(document);
    });
  } else {
    watch(document);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', remeasure, { passive: true });

  /* Images the two are sized from settle after load. */
  window.addEventListener('load', remeasure);

  document.addEventListener('shopify:section:load', function (e) {
    watch(e.target);
  });

  // The section is still attached when this fires, so the check is containment, not presence.
  document.addEventListener('shopify:section:unload', function (e) {
    panels = panels.filter(function (pair) {
      return document.contains(pair[0]) && !e.target.contains(pair[0]);
    });
  });
})();
